import React, { useEffect, useMemo } from 'react'
import Swal from 'sweetalert2'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { useCustomReducer } from '../../../../../base/customHooks'
import IPInput from '../../../../../components/IPInput'
import IPButton from '../../../../../components/IPButton'
import IPModal from '../../../../../components/IPModal'
import Table from '../../../../../components/Table'
import { CardPhoneBindingApi } from '../service/cardPhoneBindingApi'
import {
  ICardPhoneBinding,
  ICardPhoneBindingChangeHistoryItem,
  ICardPhoneBindingFilter,
  ICardPhoneVerificationHistoryItem,
} from '../models/models'

interface IState {
  filter: ICardPhoneBindingFilter
  items: ICardPhoneBinding[]
  totalCount: number
  editing: ICardPhoneBinding | null
  editPhone: string
  history: ICardPhoneVerificationHistoryItem[] | null
  changeHistory: ICardPhoneBindingChangeHistoryItem[] | null
}

/** Backend CardPhoneBindingAdminService.GetAll üst sınırı. */
const MAX_PAGE_SIZE = 200

/**
 * Kart-telefon eşleşmelerinin admin yönetimi. Kart daima maskeli gösterilir;
 * açık kart numarası bu ekrana hiç gelmez.
 */
function CardPhoneMatching(props: BaseComponentWithContextProps) {
  const [state, setState] = useCustomReducer<IState>({
    // Sayfalamayı Table bileşeni yapar; sunucunun izin verdiği en büyük sayfa çekilir.
    filter: { Page: 1, PageSize: MAX_PAGE_SIZE },
    items: [],
    totalCount: 0,
    editing: null,
    editPhone: '',
    history: null,
    changeHistory: null,
  })

  const load = async (filter: ICardPhoneBindingFilter) => {
    props.showLoading()
    try {
      const res = await new CardPhoneBindingApi({}).getAll(filter)
      if (res) setState({ items: res.Items, totalCount: res.TotalCount, filter })
    } catch (err: any) {
      Swal.fire('Hata', err.description ? err.description : 'Liste alınamadı.', 'error')
    } finally {
      props.hideLoading()
    }
  }

  useEffect(() => {
    load(state.filter)
  }, [])

  const save = async () => {
    if (!state.editing) return
    props.showLoading()
    try {
      const res = await new CardPhoneBindingApi({}).update(state.editing.Id, state.editPhone)
      if (res) {
        setState({ editing: null })
        load(state.filter)
      }
    } catch (err: any) {
      Swal.fire('Hata', err.description ? err.description : 'Güncellenemedi.', 'error')
    } finally {
      props.hideLoading()
    }
  }

  const remove = async (item: ICardPhoneBinding) => {
    const confirm = await Swal.fire({
      title: 'Eşleşme silinsin mi?',
      text: `${item.CardMask} kartının ${item.MaskedPhone} eşleşmesi silinecek. İşlem loglanır ve sns@index.com.tr adresine bildirilir.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sil',
      cancelButtonText: 'Vazgeç',
    })
    if (!confirm.isConfirmed) return

    props.showLoading()
    try {
      const res = await new CardPhoneBindingApi({}).remove(item.Id)
      if (res) load(state.filter)
    } catch (err: any) {
      Swal.fire('Hata', err.description ? err.description : 'Silinemedi.', 'error')
    } finally {
      props.hideLoading()
    }
  }

  const showHistory = async (item: ICardPhoneBinding) => {
    props.showLoading()
    try {
      const res = await new CardPhoneBindingApi({}).history(item.Id)
      setState({ history: res || [] })
    } catch (err: any) {
      Swal.fire('Hata', err.description ? err.description : 'Geçmiş alınamadı.', 'error')
    } finally {
      props.hideLoading()
    }
  }

  const showChangeHistory = async (item: ICardPhoneBinding) => {
    props.showLoading()
    try {
      const res = await new CardPhoneBindingApi({}).changeHistory(item.Id)
      setState({ changeHistory: res || [] })
    } catch (err: any) {
      Swal.fire('Hata', err.description ? err.description : 'Değişiklik geçmişi alınamadı.', 'error')
    } finally {
      props.hideLoading()
    }
  }

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString('tr-TR') : '-')

  const tableColumns = useMemo(
    () => [
      { Header: '#', accessor: 'index' },
      { Header: 'Kart', accessor: 'card' },
      { Header: 'Telefon', accessor: 'phone' },
      { Header: 'Bayi Kodu', accessor: 'dealerCode' },
      { Header: 'Bayi Ünvanı', accessor: 'dealerTitle' },
      { Header: 'İlk Doğrulama', accessor: 'firstVerified' },
      { Header: 'Son Doğrulama', accessor: 'lastVerified' },
      { Header: 'Güncelleme', accessor: 'updated' },
      { Header: 'İşlem', accessor: 'actions' },
    ],
    []
  )

  const tableData = useMemo(
    () =>
      state.items.map((item, index) => ({
        index: index + 1,
        card: item.CardMask,
        phone: item.MaskedPhone,
        dealerCode: item.DealerCode,
        dealerTitle: item.DealerTitle || '-',
        firstVerified: formatDate(item.FirstVerifiedDate),
        lastVerified: formatDate(item.LastVerifiedDate),
        updated: formatDate(item.UpdatedDate),
        actions: (
          <div style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
            <IPButton
              type='button'
              className='btn btn-primary'
              style={{ width: 'auto' }}
              text='Düzenle'
              onClick={() => setState({ editing: item, editPhone: item.Phone })}
            />
            <IPButton
              type='button'
              className='btn btn-secondary'
              style={{ width: 'auto' }}
              text='Doğrulama Geçmişi'
              onClick={() => showHistory(item)}
            />
            <IPButton
              type='button'
              className='btn btn-info'
              style={{ width: 'auto' }}
              text='Değişiklikler'
              onClick={() => showChangeHistory(item)}
            />
            <IPButton
              type='button'
              className='btn btn-danger'
              style={{ width: 'auto' }}
              text='Sil'
              onClick={() => remove(item)}
            />
          </div>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.items]
  )

  return (
    <div className=''>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header'>
            <span className='h4'>Kart - Telefon Eşleşmeleri</span>
          </div>
          <div className='card-body'>
            <div className='alert alert-info' role='alert'>
              SMS doğrulamasıyla kurulan kart - telefon eşleşmeleri listelenir. Kart numarası yalnız maskeli
              gösterilir. Telefon değişikliği ve silme işlemleri loglanır ve <b>sns@index.com.tr</b> adresine
              bilgilendirme maili gönderilir.
            </div>

            <form
              className='formbanner'
              onSubmit={e => {
                e.preventDefault()
                load({ ...state.filter, Page: 1 })
              }}
            >
              <div className='row'>
                <div className='col-lg-2'>
                  <label className='form-label'>Bayi Kodu</label>
                  <IPInput
                    type='text'
                    className='form-control'
                    name='dealerCode'
                    value={state.filter.DealerCode || ''}
                    onChange={e => setState({ filter: { ...state.filter, DealerCode: e.target.value } })}
                  />
                </div>
                <div className='col-lg-3'>
                  <label className='form-label'>Bayi Ünvanı</label>
                  <IPInput
                    type='text'
                    className='form-control'
                    name='dealerTitle'
                    value={state.filter.DealerTitle || ''}
                    onChange={e => setState({ filter: { ...state.filter, DealerTitle: e.target.value } })}
                  />
                </div>
                <div className='col-lg-2'>
                  <label className='form-label'>Telefon</label>
                  <IPInput
                    type='text'
                    className='form-control'
                    name='phone'
                    value={state.filter.Phone || ''}
                    onChange={e => setState({ filter: { ...state.filter, Phone: e.target.value } })}
                  />
                </div>
                <div className='col-lg-3'>
                  <label className='form-label'>Kart (ilk 6 / son 4)</label>
                  <IPInput
                    type='text'
                    className='form-control'
                    name='cardDigits'
                    value={state.filter.CardDigits || ''}
                    onChange={e => setState({ filter: { ...state.filter, CardDigits: e.target.value } })}
                  />
                </div>
                <div className='col-lg-2 d-flex align-items-end'>
                  <IPButton
                    type='button'
                    className='btn btn-success'
                    text='Filtrele'
                    onClick={() => load({ ...state.filter, Page: 1 })}
                  />
                </div>
              </div>
            </form>

            <br />

            <div className='d-flex justify-content-between align-items-center'>
              <b>
                {state.totalCount > state.items.length
                  ? `${state.totalCount} kaydın son ${state.items.length} tanesi listeleniyor. Daraltmak için filtre kullanınız.`
                  : `${state.items.length} kayıt listeleniyor.`}
              </b>
            </div>
            <Table columns={tableColumns} data={tableData} currentPage={0} />
          </div>
        </div>
      </div>

      <IPModal
        show={state.editing !== null}
        onClose={() => setState({ editing: null })}
        onConfirm={save}
        confirmText='Kaydet'
        title='Telefon Güncelle'
        width={420}
      >
        <div className='form-group col-md-12'>
          <label className='text-sm-left'>Cep Telefonu</label>
          <IPInput
            type='text'
            className='form-control'
            name='editPhone'
            placeholder='5XX XXX XX XX'
            value={state.editPhone}
            onChange={e => setState({ editPhone: e.target.value })}
          />
          <small className='text-muted'>
            Değişiklik loglanır ve sns@index.com.tr adresine bilgilendirme maili gönderilir.
          </small>
        </div>
      </IPModal>

      <IPModal
        show={state.changeHistory !== null}
        onClose={() => setState({ changeHistory: null })}
        hideConfirm={true}
        title='Değişiklik Geçmişi'
        width={820}
      >
        <table className='table table-sm'>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>İşlem</th>
              <th>Eski Telefon</th>
              <th>Yeni Telefon</th>
              <th>Değiştiren</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {(state.changeHistory || []).length === 0 && (
              <tr>
                <td colSpan={6}>Değişiklik kaydı yok.</td>
              </tr>
            )}
            {(state.changeHistory || []).map((h, i) => (
              <tr key={i}>
                <td>{formatDate(h.UpdateTime)}</td>
                <td>{h.ActionName}</td>
                <td>{h.OldPhone || '-'}</td>
                <td>{h.NewPhone || '-'}</td>
                <td>{h.UserName ? `${h.UserName} (${h.UserId})` : '-'}</td>
                <td>{h.UserIp || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </IPModal>

      <IPModal
        show={state.history !== null}
        onClose={() => setState({ history: null })}
        hideConfirm={true}
        title='Doğrulama Geçmişi'
        width={720}
      >
        <table className='table table-sm'>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Telefon</th>
              <th>Durum</th>
              <th>Onay IP</th>
            </tr>
          </thead>
          <tbody>
            {(state.history || []).map((h, i) => (
              <tr key={i}>
                <td>{formatDate(h.CreatedDate)}</td>
                <td>{h.MaskedPhone}</td>
                <td>{h.StatusName}</td>
                <td>{h.ConsentIp || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </IPModal>
    </div>
  )
}

export default componentWithContext(CardPhoneMatching)
